import React, { useMemo } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { Image } from '@scentregroup/ui'
import { Stack } from '@hub/stack'
import PotentialLink from '@scentregroup/shared/hub-components/potential-link'
import { getProductListingIssue } from '../../helpers/stock-level-issue-messages'
import { displayStringForPriceRange } from '../../helpers/product-price'
import { displayStringForDiscountPercentageRange } from '../../helpers/percentage'
import { stringToSlug } from '../../helpers/slug'
import { generateStoreFrontUrl, storeName } from '../../helpers/store'
import { IPercentageRange, IProductPriceRange } from '../../types'
import { IProduct } from '../products-list'
import ConditionalIssueText from '../conditional-issue-text'
import style from './index.module.scss'

export interface IProductCardProps {
  product: IProduct
  priceRange?: IProductPriceRange
  discountedPriceRange?: IProductPriceRange
  discountPercentageRange?: IPercentageRange
}

// This duplicates what's in /src/client/path.ts of the checkout app
const PRODUCT = `/shop/product/:shopId/:productId/:productTitle`

export const ProductCard: React.FunctionComponent<IProductCardProps> = ({
  product,
  priceRange,
  discountedPriceRange,
  discountPercentageRange,
}) => {
  const { shop } = product
  const issue = useMemo(() => getProductListingIssue(product?.issues), [
    product?.issues,
  ])

  const storeFrontUrl = generateStoreFrontUrl(
    shop.retailer?.sys.id,
    shop.retailer?.slug
  )

  return (
    <div className={style.productCard}>
      <Link
        className={style.productLinkWrapper}
        to={generatePath(`${PRODUCT}`, {
          productTitle: product.brand?.name
            ? stringToSlug(`${product.brand.name}-${product.name}`)
            : stringToSlug(product.name),
          productId: product.variantGroupCode,
          shopId: `${shop.id}`,
        })}
      >
        <Image
          className={style.productImage}
          imageUrl={product.image}
          alt={''}
          ratio="poster"
          bgSize="contain"
          type="mobile"
        />
        <div className={style.productName}>
          {product.brand?.name ? (
            <Stack
              gap="spacing-xs"
              aria-label={`${product.brand.name} - ${product.name}`}
            >
              <div aria-hidden>{product.brand.name}</div>
              <div aria-hidden>{product.name}</div>
            </Stack>
          ) : (
            product.name
          )}
        </div>
        <div className={style.price}>
          {displayStringForPriceRange(discountedPriceRange ?? priceRange)}
        </div>
      </Link>
      <div className={style.productRetailer}>
        Sold by&nbsp;
        <PotentialLink href={storeFrontUrl}>
          {storeName(shop.name, shop.retailer?.title)}
        </PotentialLink>
      </div>
      <ConditionalIssueText issue={issue} wrapperClass={style.issueWrapper} />
      {discountedPriceRange && (
        <div className={style.originalPrice}>
          {displayStringForPriceRange(priceRange)}
        </div>
      )}
      {discountPercentageRange && (
        <div className={style.discount}>
          {displayStringForDiscountPercentageRange(discountPercentageRange)}
        </div>
      )}
    </div>
  )
}

export default ProductCard
